import { API, apiUrl } from '@/config';
import { ERRORS } from '@/constants';
import type { ApiErrorBody, FieldErrors } from '@/types';

/**
 * The only place this portal talks to the network.
 *
 * ── What is missing here, and why ───────────────────────────────────────────
 *
 * No access token. No refresh. No 401-retry loop. No token storage. The other
 * three portals have all of it and this one has none, mirroring the server:
 * `api/review` does not import `authenticate` at all, and the capability to
 * hold a session is absent rather than merely unused.
 *
 * The share token is passed in per call and travels in a header. It is not
 * cached here, not written to storage, and not held in a module-level variable
 * that some future call could pick up by accident - it comes from the URL the
 * dentist opened, which is the only place it exists on this side.
 *
 * A 401 here therefore means the link needs a passcode or the passcode was
 * wrong. There is nothing to refresh and nothing to retry.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly fieldErrors: FieldErrors | undefined;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error.code ?? 'unknown';
    this.requestId = body?.error.requestId ?? '';
    this.fieldErrors = isFieldErrors(body?.error.details) ? body.error.details : undefined;
  }

  /** The link is real but locked. The page should ask for the passcode. */
  get needsPasscode(): boolean {
    return this.code === 'passcode_required' || this.code === 'passcode_invalid';
  }

  /**
   * The link will never work again, whatever the dentist does.
   *
   * Withdrawn, expired or never real. Grouped because the page's answer to all
   * three is the same: tell them to ask the lab for a new link. Which of the
   * three it was still reaches them in the message, because "this expired" and
   * "this was withdrawn" mean different things to the person reading it.
   */
  get isDead(): boolean {
    return (
      this.code === 'share_link_expired' ||
      this.code === 'share_link_revoked' ||
      this.status === 404
    );
  }
}

function isFieldErrors(value: unknown): value is FieldErrors {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every((v) => Array.isArray(v));
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  signal?: AbortSignal;
  /** The share token from the URL. Every call needs it; nothing caches it. */
  token: string;
}

async function parseError(response: Response, fallback: string): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
  return new ApiError(response.status, body, fallback);
}

async function send<T>(path: string, options: RequestOptions): Promise<T> {
  const { method = 'GET', body, signal, token } = options;

  const timeout = AbortSignal.timeout(API.timeoutMs);
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;

  const headers: Record<string, string> = { 'x-share-token': token };
  if (body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      headers,
      /*
       * No credentials. There is no cookie to send and none to receive - this
       * portal has no session by design, and asking for cookies would be the
       * first step towards accidentally acquiring one.
       */
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      signal: combined,
    });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw new ApiError(0, null, ERRORS.network);
  }

  if (!response.ok) throw await parseError(response, ERRORS.generic);
  if (response.status === 204) return undefined as T;

  const payload = (await response.json()) as { data: T };
  return payload.data;
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body: unknown, options: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'POST', body }),
};
